import React from 'react';
import { Icon } from '@iconify/react';
import type { MappedLeadData } from '../../types';
import { Card, CardHeader, SectionLabel, SimpleTabContent } from '../common/Atomic';
import {
    buildCompanyOpportunityGroups,
    buildCompanyTextGroups,
    formatMoneyValue,
    formatTimeline,
    getOpportunityRecords,
    getOpportunityTone,
    humanizeKey,
    isUsableText,
    mergeCompanySummaries,
    toDisplayItems,
    uniqueItems,
} from './companyInsights';
import { CampaignOpportunitiesNotice } from './CampaignOpportunitiesNotice';

interface OpportunitiesTabProps {
    data: MappedLeadData;
}

/** Rich card for one structured opportunity (campaign-scoped or general).
 *  Shared by both sections so the two sources render identically. */
const OpportunityCard: React.FC<{ opportunity: Record<string, any>; index: number }> = ({ opportunity, index }) => {
    const evidence = uniqueItems(toDisplayItems(opportunity.evidence));
    const keywords = uniqueItems(toDisplayItems(opportunity.keywords));
    const moneyValue = formatMoneyValue(opportunity.amount_usd);
    const timeline = formatTimeline(opportunity.timeline_months);
    const titleParts = [opportunity.type, opportunity.category]
        .filter(isUsableText)
        .map((value) => humanizeKey(value));

    return (
        <article
            className="min-w-0 rounded-xl border border-emerald-300/10 bg-black/15 p-3"
        >
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.14em] text-emerald-300/70">
                        <Icon icon="bx:target-lock" className="h-3.5 w-3.5" aria-hidden="true" />
                        Opportunity {index + 1}
                    </div>
                    <h4 className="mt-1 break-words text-sm font-semibold text-white">
                        {titleParts.join(' · ') || 'Commercial Opportunity'}
                    </h4>
                </div>
                <div className="flex flex-wrap justify-end gap-1.5">
                    {isUsableText(opportunity.urgency) && (
                        <span className={`rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] ${getOpportunityTone(opportunity.urgency)}`}>
                            {opportunity.urgency}
                        </span>
                    )}
                    {(typeof opportunity.confidence === 'number' || isUsableText(opportunity.confidence)) && (
                        <span className="rounded-md border border-white/10 bg-white/[0.035] px-1.5 py-0.5 text-[9px] font-semibold text-gray-400">
                            {opportunity.confidence}% confidence
                        </span>
                    )}
                </div>
            </div>

            {isUsableText(opportunity.need) && (
                <p className="mt-2 break-words text-[13px] leading-5 text-gray-200">{opportunity.need}</p>
            )}

            {(moneyValue || timeline || isUsableText(opportunity.decision_maker) || isUsableText(opportunity.country)) && (
                <dl className="mt-2 grid grid-cols-1 gap-x-3 gap-y-1.5 border-t border-white/[0.06] pt-2 sm:grid-cols-2">
                    {moneyValue && (
                        <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-gray-400">
                            <Icon icon="bx:dollar-circle" className="h-3.5 w-3.5 shrink-0 text-emerald-300/70" aria-hidden="true" />
                            <dt className="sr-only">Estimated value</dt>
                            <dd className="break-words">{moneyValue}</dd>
                        </div>
                    )}
                    {timeline && (
                        <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-gray-400">
                            <Icon icon="bx:time-five" className="h-3.5 w-3.5 shrink-0 text-cyan-300/70" aria-hidden="true" />
                            <dt className="sr-only">Timeline</dt>
                            <dd className="break-words">{timeline}</dd>
                        </div>
                    )}
                    {isUsableText(opportunity.decision_maker) && (
                        <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-gray-400">
                            <Icon icon="bx:user-voice" className="h-3.5 w-3.5 shrink-0 text-violet-300/70" aria-hidden="true" />
                            <dt className="sr-only">Decision maker</dt>
                            <dd className="break-words">{humanizeKey(opportunity.decision_maker)}</dd>
                        </div>
                    )}
                    {isUsableText(opportunity.country) && (
                        <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-gray-400">
                            <Icon icon="bx:map" className="h-3.5 w-3.5 shrink-0 text-amber-300/70" aria-hidden="true" />
                            <dt className="sr-only">Market</dt>
                            <dd className="break-words">{opportunity.country}</dd>
                        </div>
                    )}
                </dl>
            )}

            {evidence.length > 0 && (
                <div className="mt-2 border-t border-white/[0.06] pt-2">
                    <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.13em] text-gray-500">
                        <Icon icon="bx:search-alt" className="h-3 w-3 text-cyan-300/70" aria-hidden="true" />
                        Evidence
                    </div>
                    <ul className="space-y-1">
                        {evidence.map((item) => (
                            <li key={item} className="flex items-start gap-2 text-[12px] leading-4.5 text-gray-400">
                                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-cyan-300/60" aria-hidden="true" />
                                <span className="min-w-0 break-words">{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {keywords.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Opportunity keywords">
                    {keywords.map((keyword) => (
                        <span key={keyword} className="rounded-md bg-white/[0.045] px-1.5 py-1 text-[9px] font-semibold text-gray-400">
                            {keyword}
                        </span>
                    ))}
                </div>
            )}
        </article>
    );
};

/** Header row shared by both opportunity sections. `badge` marks the
 *  campaign-scoped section as the primary signal. */
const OpportunitySectionHeader: React.FC<{ label: string; subtitle: string; labelClassName: string; count: number; badge?: React.ReactNode }> = ({
    label,
    subtitle,
    labelClassName,
    count,
    badge,
}) => (
    <div className="mb-2.5 flex items-end justify-between gap-3">
        <div>
            <div className="flex flex-wrap items-center gap-2">
                <SectionLabel label={label} className={`mb-0 ${labelClassName}`} />
                {badge}
            </div>
            <p className="mt-0.5 text-[11px] text-gray-500">{subtitle}</p>
        </div>
        <span className="shrink-0 text-[10px] font-semibold text-gray-500">
            {count} {count === 1 ? 'opportunity' : 'opportunities'}
        </span>
    </div>
);

export const OpportunitiesTab: React.FC<OpportunitiesTabProps> = ({ data }) => {
    const companySummary = mergeCompanySummaries(data.rawCompanySummary, data.companySummary);
    const leadSummary = data.rawLeadSummary && typeof data.rawLeadSummary === 'object'
        ? data.rawLeadSummary as Record<string, unknown>
        : null;
    // Campaign-specific opportunities (lvl2) rank first; general person ones (lvl1) below.
    const campaignOpportunities = getOpportunityRecords(data.campaignOpportunities);
    const generalOpportunities = getOpportunityRecords(data.opportunities);
    const personOpportunityGroups = buildCompanyTextGroups(leadSummary?.opportunities);
    const structuredCompanyOpportunityGroups = buildCompanyOpportunityGroups(companySummary?.opportunities);
    const fallbackGrowthItems = uniqueItems(toDisplayItems(companySummary?.opportunities));
    const companyOpportunityGroups = structuredCompanyOpportunityGroups.length > 0
        ? structuredCompanyOpportunityGroups
        : fallbackGrowthItems.length > 0
            ? [{
                key: 'growth-directions',
                label: 'Growth Directions',
                items: fallbackGrowthItems,
                icon: 'bx:bulb',
            }]
            : [];
    // The person summary and company summary carry complementary engagement
    // guidance. Previously only the company half was rendered, dropping the
    // lead's ideal approach, topics, conversation starters, and value alignment.
    const structuredEngagementStrategyGroups = [
        ...buildCompanyTextGroups(leadSummary?.engagement_strategy),
        ...buildCompanyTextGroups(companySummary?.engagement_strategy),
    ];
    const engagementGroupsByKey = new Map<string, typeof structuredEngagementStrategyGroups[number]>();
    structuredEngagementStrategyGroups.forEach((group) => {
        const existing = engagementGroupsByKey.get(group.key);
        engagementGroupsByKey.set(group.key, existing
            ? { ...existing, items: uniqueItems([...existing.items, ...group.items]) }
            : group);
    });
    const mergedEngagementStrategyGroups = Array.from(engagementGroupsByKey.values());
    const fallbackEngagementItems = uniqueItems([
        ...toDisplayItems(leadSummary?.engagement_strategy),
        ...toDisplayItems(companySummary?.engagement_strategy),
    ]);
    const engagementStrategyGroups = mergedEngagementStrategyGroups.length > 0
        ? mergedEngagementStrategyGroups
        : fallbackEngagementItems.length > 0
            ? [{ key: 'recommended-approach', label: 'Recommended Approach', items: fallbackEngagementItems }]
            : [];
    const insightCount = campaignOpportunities.length + generalOpportunities.length + personOpportunityGroups.length + companyOpportunityGroups.length;
    const hasGrowthOpportunityData = insightCount > 0;

    return (
        <SimpleTabContent>
            <div className="space-y-4">
                <Card className="border-emerald-400/10 bg-[linear-gradient(180deg,rgba(16,185,129,0.045),rgba(11,14,20,0.96))]">
                    <CardHeader
                        title="Growth Opportunities"
                        icon={<Icon icon="bx:trending-up" className="h-5 w-5" aria-hidden="true" />}
                        iconColor="text-emerald-300"
                    >
                        {hasGrowthOpportunityData && (
                            <span className="rounded-md border border-emerald-300/15 bg-emerald-300/[0.06] px-2 py-1 text-[10px] font-bold text-emerald-100/70">
                                {insightCount} {insightCount === 1 ? 'insight' : 'insights'}
                            </span>
                        )}
                    </CardHeader>

                    {hasGrowthOpportunityData ? (
                        <>
                            {/* The campaign slot is ALWAYS visible: when nothing
                                campaign-scoped was detected, an explicit notice
                                replaces the highlighted block so the profile /
                                market lists below are not mistaken for it. */}
                            {campaignOpportunities.length > 0 ? (
                                <section aria-label="Campaign opportunities">
                                    <div className="rounded-2xl border border-emerald-300/20 bg-emerald-300/[0.045] p-3">
                                        <OpportunitySectionHeader
                                            label="Campaign Opportunities"
                                            subtitle="Specifically detected for this campaign."
                                            labelClassName="text-emerald-300"
                                            count={campaignOpportunities.length}
                                            badge={
                                                <span className="inline-flex items-center gap-1 rounded-md border border-emerald-300/30 bg-emerald-300/[0.12] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-emerald-200">
                                                    <Icon icon="bx:target-lock" className="h-3 w-3" aria-hidden="true" />
                                                    Main
                                                </span>
                                            }
                                        />
                                        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                                            {campaignOpportunities.map((opportunity, index) => (
                                                <OpportunityCard
                                                    key={opportunity.id || `campaign-${index}`}
                                                    opportunity={opportunity}
                                                    index={index}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </section>
                            ) : <CampaignOpportunitiesNotice />}

                            {generalOpportunities.length > 0 && (
                                <section
                                    className={campaignOpportunities.length > 0 ? 'mt-4 border-t border-white/[0.06] pt-3' : ''}
                                    aria-label="General commercial opportunities"
                                >
                                    <OpportunitySectionHeader
                                        label="General Opportunities"
                                        subtitle="Detected from the person's profile, independent of this campaign."
                                        labelClassName="text-emerald-300"
                                        count={generalOpportunities.length}
                                    />
                                    <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                                        {generalOpportunities.map((opportunity, index) => (
                                            <OpportunityCard
                                                key={opportunity.id || `general-${index}`}
                                                opportunity={opportunity}
                                                index={index}
                                            />
                                        ))}
                                    </div>
                                </section>
                            )}

                            {personOpportunityGroups.length > 0 && (
                                <section
                                    className={(campaignOpportunities.length > 0 || generalOpportunities.length > 0) ? 'mt-4 border-t border-white/[0.06] pt-3' : ''}
                                    aria-label="Person opportunity signals"
                                >
                                    <div className="mb-2.5">
                                        <SectionLabel label="Person Opportunity Signals" className="mb-0 text-cyan-300" />
                                        <p className="mt-0.5 text-[11px] text-gray-500">Projects, development areas, and collaboration potential identified from the person profile.</p>
                                    </div>
                                    <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${personOpportunityGroups.length > 2 ? 'xl:grid-cols-3' : ''}`}>
                                        {personOpportunityGroups.map((group) => (
                                            <div key={group.key} className="min-w-0 rounded-xl border border-cyan-300/[0.08] bg-cyan-300/[0.025] p-3">
                                                <h4 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em] text-cyan-100/80">
                                                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-cyan-300/[0.08] text-cyan-200">
                                                        <Icon icon="bx:user-voice" className="h-3.5 w-3.5" aria-hidden="true" />
                                                    </span>
                                                    {group.label}
                                                </h4>
                                                <ul className="mt-2 space-y-1.5">
                                                    {group.items.map((item) => (
                                                        <li key={item} className="flex items-start gap-2 text-[12px] leading-4.5 text-gray-300">
                                                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-cyan-300/65" aria-hidden="true" />
                                                            <span className="min-w-0 break-words">{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {companyOpportunityGroups.length > 0 && (
                                <section
                                    className={(campaignOpportunities.length > 0 || generalOpportunities.length > 0 || personOpportunityGroups.length > 0) ? 'mt-4 border-t border-white/[0.06] pt-3' : ''}
                                    aria-label="Company growth directions"
                                >
                                    <SectionLabel label="Company Growth Directions" className="mb-2.5 text-emerald-300" />
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                        {companyOpportunityGroups.map((group) => (
                                            <div key={group.key} className="min-w-0 rounded-xl border border-white/[0.055] bg-white/[0.025] p-3">
                                                <h4 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em] text-emerald-200/85">
                                                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-emerald-300/[0.07] text-emerald-200">
                                                        <Icon icon={group.icon} className="h-3.5 w-3.5" aria-hidden="true" />
                                                    </span>
                                                    {group.label}
                                                </h4>
                                                <ul className="mt-2 space-y-1.5">
                                                    {group.items.map((item) => (
                                                        <li key={item} className="flex items-start gap-2 text-[12px] leading-4.5 text-gray-300">
                                                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-emerald-300/70" aria-hidden="true" />
                                                            <span className="min-w-0 break-words">{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </>
                    ) : (
                        <>
                            {/* Fully empty tab still distinguishes the two sources:
                                nothing campaign-scoped AND nothing at all. */}
                            <CampaignOpportunitiesNotice />
                            <div className="mt-4 rounded-xl border border-dashed border-white/10 px-4 py-8 text-center">
                                <Icon icon="bx:trending-up" className="mx-auto h-6 w-6 text-gray-600" aria-hidden="true" />
                                <p className="mt-2 text-[13px] text-gray-500">No growth opportunities have been identified yet.</p>
                            </div>
                        </>
                    )}
                </Card>

                {engagementStrategyGroups.length > 0 && (
                    <Card>
                        <CardHeader
                            title="Recommended Engagement Strategy"
                            icon={<Icon icon="bx:conversation" className="h-5 w-5" aria-hidden="true" />}
                            iconColor="text-purple-400"
                        />
                        <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${engagementStrategyGroups.length > 2 ? 'xl:grid-cols-3' : ''}`}>
                            {engagementStrategyGroups.map((group) => (
                                <div key={group.key} className="min-w-0 rounded-xl bg-white/[0.025] p-3">
                                    <SectionLabel label={group.label} className="text-purple-300" />
                                    {group.items.length === 1 ? (
                                        <p className="break-words text-[13px] leading-5 text-gray-300">{group.items[0]}</p>
                                    ) : (
                                        <ul className="space-y-1">
                                            {group.items.map((item) => (
                                                <li key={item} className="flex items-start gap-2 text-[12px] leading-4.5 text-gray-300">
                                                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-purple-300/70" aria-hidden="true" />
                                                    <span className="min-w-0 break-words">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Card>
                )}
            </div>
        </SimpleTabContent>
    );
};
